import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { formatOptionsList } from '../../../../../../features/formatters'
import useLoadInstagramAccounts from '../../../../../../hooks/instagramAccountsHooks/useLoadInstagramAccounts'
import useGetInstagramAccounts from '../../../../../../hooks/instagramAccountsHooks/useGetInstagramAccounts'

import { getAccessibleSocialPages, getFacebookPages } from '../../../../../../modules/actions/socialAccounts'
import {
  accessibleSocialPagesErrorSelector,
  accessibleSocialPagesIsLoadingSelector,
  accessibleSocialPagesSelector,
  accessibleSocialPagesWasLoadedSelector,
  facebookPagesErrorSelector,
  facebookPagesIsLoadingSelector,
  facebookPagesSelector,
  facebookPagesWasLoadedSelector
} from '../../../../../../modules/selectors/socialAccounts'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { FB_PAGE_CHOICE, FB_SPONSOR_PAGE_ID, INSTA_ACCOUNT_CHOICE, INSTAGRAM_ACCOUNT_SPONSOR_ID } from '../../../fields'
import { FACEBOOK_PROVIDER_PLATFORM } from '../../../../../../constants/selectLists/platformList'

export default function usePreselectFacebookPageAndInstagramAccount(formik) {
  const dispatch = useDispatch()

  const accessibleSocialPages = useSelector(accessibleSocialPagesSelector)
  const accessibleSocialPagesLoadError = useSelector(accessibleSocialPagesErrorSelector)
  const accessibleSocialPagesIsLoading = useSelector(accessibleSocialPagesIsLoadingSelector)
  const accessibleSocialPagesWasLoaded = useSelector(accessibleSocialPagesWasLoadedSelector)

  const facebookPages = useSelector(facebookPagesSelector)
  const facebookPagesLoadError = useSelector(facebookPagesErrorSelector)
  const facebookPagesIsLoading = useSelector(facebookPagesIsLoadingSelector)
  const facebookPagesWasLoaded = useSelector(facebookPagesWasLoadedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  const [facebookPageWasPreselected, setFacebookPageWasPreselected] = useState(false)

  const { values, setFieldValue } = formik

  // const selectedFacebookPage = facebookPages.find(item => item.id === values[FB_PAGE_CHOICE])
  const selectedSponsorFacebookPage = facebookPages.find(item => item.id === values[FB_SPONSOR_PAGE_ID])

  const selectedSponsorFacebookPageInstagramAccount = selectedSponsorFacebookPage?.instagram_business_account_id

  useLoadInstagramAccounts({
    facebookPageId: values[FB_PAGE_CHOICE],
    shouldLoadPageAccessToken: true,
    shouldLoadInstagramAccounts: true
  })

  const { selectedInstagramAccounts: selectedInstagramAccountsPageMain } = useGetInstagramAccounts(
    values[FB_PAGE_CHOICE]
  )

  const { results: mainFacebookPageInstagramAccounts } = selectedInstagramAccountsPageMain

  const formattedFacebookPages = useMemo(() => {
    return formatOptionsList({
      list: accessibleSocialPages,
      valueName: 'external_id',
      labelName: 'name'
    })
  }, [accessibleSocialPages])

  // fetch accessible facebook pages
  useEffect(() => {
    // the error is checked to avoid infinite loop of requests making on error as on error !accessibleSocialPagesWasLoaded && !accessibleSocialPagesIsLoading become true again
    if (!accessibleSocialPagesWasLoaded && !accessibleSocialPagesIsLoading && !accessibleSocialPagesLoadError) {
      dispatch(
        getAccessibleSocialPages({
          platform: FACEBOOK_PROVIDER_PLATFORM
        })
      )
    }
  }, [dispatch, accessibleSocialPagesWasLoaded, accessibleSocialPagesIsLoading, accessibleSocialPagesLoadError])

  // fetch facebook pages
  // we need to fetch facebook pages because they have instagram accounts linked to them,
  // and accessible facebook pages don't have instagram accounts linked to them
  useEffect(() => {
    if (!facebookPagesWasLoaded && !facebookPagesIsLoading && !facebookPagesLoadError) {
      dispatch(getFacebookPages({ account: selectedAdAccountId }))
    }
  }, [dispatch, facebookPagesWasLoaded, facebookPagesIsLoading, facebookPagesLoadError, selectedAdAccountId])

  // Preselect first facebook page
  useEffect(() => {
    if (
      accessibleSocialPages.length &&
      !facebookPageWasPreselected &&
      // Facebook page may be preselected on lineItem level, make sure it's not already preselected
      !values[FB_PAGE_CHOICE]
    ) {
      setFieldValue(FB_PAGE_CHOICE, formattedFacebookPages[0].value)
      setFacebookPageWasPreselected(true)
    }
  }, [accessibleSocialPages, setFieldValue, facebookPageWasPreselected, formattedFacebookPages, values])

  // set instagram account if exists
  // we set regular instagram (instagram_user_id) from endpoint GET/facebook/pages/{id}/instagram_accounts/
  useEffect(() => {
    if (mainFacebookPageInstagramAccounts?.length) {
      // set instagram account id to formik if it exists, when facebook page is selected
      setFieldValue(INSTA_ACCOUNT_CHOICE, mainFacebookPageInstagramAccounts[0].id)
    } else {
      // clear if page has no instagram account
      setFieldValue(INSTA_ACCOUNT_CHOICE, '')
    }
  }, [setFieldValue, mainFacebookPageInstagramAccounts])

  // set sponsor facebook page linked instagram account if exists
  // we set sponsor instagram (sponsor_id) from endpoint GET/facebook/pages/
  useEffect(() => {
    if (selectedSponsorFacebookPageInstagramAccount) {
      setFieldValue(INSTAGRAM_ACCOUNT_SPONSOR_ID, selectedSponsorFacebookPageInstagramAccount)
    } else {
      setFieldValue(INSTAGRAM_ACCOUNT_SPONSOR_ID, '')
    }
  }, [setFieldValue, selectedSponsorFacebookPageInstagramAccount])
}
